import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import {
    useIsHandRaised,
    useIsOngoingCheckpoint,
    useIsSoundMode,
    useSoundModeParticipantCount,
} from "@common/hooks/livekit/useParticipantState"
import {
    setLastPopupTime,
    setOngoingCheckpointIds,
    setShowRaiseHandCheckpoint,
} from "@data/redux/actions/liveMode"
import { hasRole } from "@data/utility/ability"
import { hasFeature } from "@data/utility/Utils"
import moment from "moment"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

const SILENT_MODE_THRESHOLD_MINUTES = 10
const CHECKPOINT_INTERVAL_MINUTES = 1

export const useTutorCheckpointManager = ({
    user,
    liveSessionId,
    currentUser,
    activeTime = 0,
    isTimeSinceLastCallAlert = false,
    currentLiveSession,
    skipCheckpoint = false,
    isStudentOnline = true,
}) => {
    const [showCheckpoint, setShowCheckpoint] = useState(false)
    const [popupType, setPopupType] = useState(null)
    const dispatch = useDispatch()
    const { sendLiveKitData: triggerLiveKitEvent } = useLiveKitEventSender()

    // Per-user selectors (primitives — no unnecessary re-renders)
    const isSoundOn = useIsSoundMode(user?.id)
    const isInSilentMode = !isSoundOn
    const isHandRaised = useIsHandRaised(user?.id)
    const hasOngoingCheckpoint = useIsOngoingCheckpoint(user?.id)
    const soundModeParticipantCount = useSoundModeParticipantCount()

    const lastPopupTimes = useSelector(
        (state) => state.liveMode.lastPopupTimes || {}
    )
    const showRaiseHandCheckpoint = useSelector((state) => {
        return state.liveMode.showRaiseHandCheckpoint?.[user?.id] || false
    })

    // Initialize user conversations with proper null checks
    const userConversations = user?.conversations || []

    const currentStudentActivity = useSelector((state) =>
        state.liveMode?.currentStudentActivity || {}
    )

    const isV4 = hasFeature(currentLiveSession?.live_room, "checkpoint-rh")

    // Helper function to check if conversation exists for current activity
    const hasExistingConversationForActivity = () => {
        const currentActivityId = currentStudentActivity[user?.id]
        if (!currentActivityId) return false


        const hasCompletedConversation = userConversations.some(conv => {
            return conv?.student_id === user?.id &&
                conv?.activity_id === currentActivityId &&
                conv.status !== "ongoing"
        })

        return hasCompletedConversation
    }

    // Helper function to check if enough time has passed since last checkpoint
    const hasEnoughTimePassedSinceLastCheckpoint = () => {
        if (!userConversations || userConversations.length === 0) return true

        const sessionConversations = userConversations
            .filter(conv => conv?.live_session_id === liveSessionId && conv?.student_id === user.id && conv.status !== "ongoing" && conv?.trigger_context !== "prerequisite_quiz")
            .sort((a, b) => moment(b.created_at).diff(moment(a.created_at)))


        if (sessionConversations.length === 0) return true

        const mostRecentConversation = sessionConversations[0]
        const lastCheckpointTime = moment(mostRecentConversation?.ended_at ?? mostRecentConversation.created_at)
        const timeSinceLastCheckpoint = moment().diff(lastCheckpointTime, "minutes")


        return timeSinceLastCheckpoint >= CHECKPOINT_INTERVAL_MINUTES
    }

    useEffect(() => {
        if (
            !hasRole(currentUser, "tutor") ||
            !user?.id ||
            !liveSessionId ||
            !isV4 ||
            skipCheckpoint ||
            !isStudentOnline
        ) {
            setShowCheckpoint(false)
            setPopupType(null)
            return
        }

        const lastPopupTime = lastPopupTimes[liveSessionId]?.[user?.id]

        const timeSinceLastPopup = lastPopupTime
            ? moment().diff(moment(lastPopupTime), "minutes")
            : Infinity

        const shouldShowSilentModePopup =
            activeTime > 0 &&
            isInSilentMode &&
            isTimeSinceLastCallAlert &&
            timeSinceLastPopup >= SILENT_MODE_THRESHOLD_MINUTES

        const shouldShowRaiseHandPopup = showRaiseHandCheckpoint && isHandRaised

        const shouldShowPopup = hasEnoughTimePassedSinceLastCheckpoint() && !hasExistingConversationForActivity() &&
            (shouldShowRaiseHandPopup || shouldShowSilentModePopup) &&
            isInSilentMode &&
            !hasOngoingCheckpoint && soundModeParticipantCount > 0

        setShowCheckpoint(shouldShowPopup);
        setPopupType(shouldShowPopup ? (shouldShowRaiseHandPopup ? 'raise_hand' : 'silent_mode_10min') : null);

    }, [
        isInSilentMode,
        isHandRaised,
        showRaiseHandCheckpoint,
        activeTime,
        isTimeSinceLastCallAlert,
        user?.id,
        liveSessionId,
        hasOngoingCheckpoint,
        lastPopupTimes,
        isV4,
        isStudentOnline,
        currentStudentActivity,
        user?.todo_activities,
        user?.activities,
        soundModeParticipantCount,
    ])

    const handleCheckpointAction = (accepted, source = 'tutor_accept') => {
        if (!showCheckpoint || !isV4) return

        setShowCheckpoint(false)

        dispatch(setLastPopupTime(user.id, liveSessionId, moment().valueOf()))
        dispatch(setShowRaiseHandCheckpoint(user.id, false))

        if (accepted) {
            dispatch(setOngoingCheckpointIds([user.id], "add"))
        }

        triggerLiveKitEvent(
            "checkpointTriggerResponse",
            {
                studentId: user.id,
                accepted: accepted,
                tutorId: currentUser?.id,
                trigger_context: popupType,
            trigger_source: source,
            timestamp: Date.now(),
            },
            [user.id]
        )
    }

    return {
        showCheckpointPopup: showCheckpoint && isV4,
        isSilentModePopup: popupType === 'silent_mode_10min',
        handleCheckpointAction,
    }
}
